import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, DataGrid, KpiCard, Modal,
  type DataGridColumn,
} from '@kss/ui';
import {
  Activity, AlertTriangle, Download, FileSearch, RefreshCw, Shield,
  ShieldAlert, UserCheck, X,
} from 'lucide-react';
import { auditLogService, type AuditLog as AuditLogRow } from '@kss/api';

/**
 * Audit-log explorer.
 *
 * The live `/audit/AuditLog` is an HTTP ACCESS log (eventAt, actorUsername,
 * httpMethod, resourcePath, controllerName/actionName, statusCode, severity,
 * errorMessage, …), NOT an entity-change CRUD log. This page reads those real
 * columns and filters CLIENT-SIDE (the endpoint doesn't honour query-param
 * filters), since the server returns the full set.
 */

type PresetKey = '24h' | '7d' | '30d' | 'all';
const PRESETS: { key: PresetKey; label: string; hours: number }[] = [
  { key: '24h', label: 'Last 24h',     hours: 24 },
  { key: '7d',  label: 'Last 7 days',  hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: 'all', label: 'All',          hours: 0 },
];
const SEVERITIES = ['', 'Information', 'Warning', 'Error', 'Critical'];

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

const eventAtOf = (r: AuditLogRow): string => String(r.eventAt ?? r.createdAt ?? r.indate ?? '');
const sevOf = (r: AuditLogRow): string => String(r.severity ?? '').toLowerCase();
const isError = (r: AuditLogRow): boolean => sevOf(r) === 'error' || sevOf(r) === 'critical' || Number(r.statusCode ?? 0) >= 400 || !!r.errorMessage;

export default function AuditLog() {
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('');
  const [preset, setPreset] = useState<PresetKey>('all');
  const [selected, setSelected] = useState<AuditLogRow | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      // The endpoint ignores query-param filters → fetch all, filter client-side.
      const data = await auditLogService.filter({});
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = preset === 'all' ? 0 : Date.now() - (PRESETS.find((p) => p.key === preset)?.hours ?? 0) * 3600_000;
    return rows.filter((r) => {
      if (severity && sevOf(r) !== severity.toLowerCase()) return false;
      if (cutoff) { const t = Date.parse(eventAtOf(r)); if (!Number.isNaN(t) && t < cutoff) return false; }
      if (q) {
        const hay = `${r.name ?? ''} ${r.actorUsername ?? ''} ${r.controllerName ?? ''} ${r.actionName ?? ''} ${r.resourcePath ?? ''} ${r.ipAddress ?? ''} ${r.errorMessage ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => Date.parse(eventAtOf(b)) - Date.parse(eventAtOf(a)));
  }, [rows, search, severity, preset]);

  const kpis = useMemo(() => {
    const last24 = Date.now() - 24 * 3600_000;
    let errors = 0; const actors = new Set<string>();
    for (const r of filtered) {
      if (isError(r)) errors++;
      const t = Date.parse(eventAtOf(r));
      if (!Number.isNaN(t) && t >= last24 && r.actorUsername) actors.add(String(r.actorUsername));
    }
    return { total: filtered.length, errors, actors: actors.size };
  }, [filtered]);

  async function exportCsv() {
    setExporting(true); setErr(null);
    try {
      const headers = ['eventAt', 'name', 'severity', 'actorUsername', 'httpMethod', 'resourcePath', 'controllerName', 'actionName', 'statusCode', 'durationMs', 'ipAddress', 'errorMessage'];
      const lines = [headers.join(',')];
      for (const r of filtered) lines.push(headers.map((h) => csvEscape((r as unknown as Record<string, unknown>)[h])).join(','));
      downloadBlob(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }), `audit-log-${todayIso()}.csv`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Export failed');
    } finally { setExporting(false); }
  }

  const columns = useMemo<DataGridColumn<AuditLogRow>[]>(
    () => [
      { accessor: 'eventAt', title: 'When', sortable: true, width: 168,
        render: (r) => (
          <div className="leading-tight">
            <div>{eventAtOf(r) ? new Date(eventAtOf(r)).toLocaleString() : '—'}</div>
            <div className="text-[10px] text-content-muted">{relativeTime(eventAtOf(r))}</div>
          </div>
        ) },
      { accessor: 'name', title: 'Event', sortable: true, filterable: true,
        render: (r) => <span className="font-medium">{String(r.name ?? r.actionName ?? '—')}</span> },
      { accessor: 'severity', title: 'Severity', sortable: true, width: 120,
        render: (r) => <SeverityPill v={String(r.severity ?? (isError(r) ? 'Error' : 'Information'))} /> },
      { accessor: 'actorUsername', title: 'Actor', filterable: true, width: 150,
        render: (r) => (
          <span className="inline-flex items-center gap-1">
            <UserCheck size={11} className="text-content-muted" /> {String(r.actorUsername ?? '—')}
          </span>
        ) },
      { accessor: 'resourcePath', title: 'Method · Path', filterable: true,
        render: (r) => (
          <span className="line-clamp-1 max-w-[360px] text-content-secondary">
            {r.httpMethod ? <code className="mr-1 text-[10px] font-semibold text-primary">{String(r.httpMethod)}</code> : null}
            {String(r.resourcePath ?? `${r.controllerName ?? ''}·${r.actionName ?? ''}`) || '—'}
          </span>
        ) },
      { accessor: 'statusCode', title: 'Status', sortable: true, width: 80,
        render: (r) => {
          const c = Number(r.statusCode ?? 0);
          if (!c) return <span className="text-content-muted">—</span>;
          const cls = c >= 500 ? 'text-danger' : c >= 400 ? 'text-warning' : 'text-success';
          return <span className={`font-medium ${cls}`}>{c}</span>;
        } },
      { accessor: 'ipAddress', title: 'IP', width: 120, render: (r) => String(r.ipAddress ?? '—') },
    ],
    [],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Audit log</h1>
          <p className="text-xs text-content-muted">
            Access trail of every API action — who did what, when, from where, and the result.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button size="sm" onClick={exportCsv} disabled={exporting || filtered.length === 0}>
          <Download className="h-3.5 w-3.5" /> {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard title="Events in view" value={kpis.total.toLocaleString()}
          subtitle={PRESETS.find((p) => p.key === preset)?.label}
          icon={<Activity className="h-5 w-5 text-info" />} />
        <KpiCard title="Errors / failures" value={kpis.errors.toLocaleString()}
          subtitle={kpis.errors ? 'Investigate' : 'All clear'}
          icon={kpis.errors ? <ShieldAlert className="h-5 w-5 text-danger" /> : <Shield className="h-5 w-5 text-success" />} />
        <KpiCard title="Active users (24h)" value={kpis.actors.toLocaleString()}
          subtitle="Distinct actors"
          icon={<AlertTriangle className="h-5 w-5 text-primary" />} />
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => setPreset(p.key)}
              className={`h-7 rounded-full border px-3 text-[11px] ${preset === p.key ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-surface-card text-content-secondary hover:border-primary'}`}>
              {p.label}
            </button>
          ))}
          <label className="ml-auto flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Severity</span>
            <select className={inputCls} value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s || 'All severities'}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Search</span>
            <input className={inputCls} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="actor, path, event, IP…" />
          </label>
        </div>
        <div className="mb-2 text-xs text-content-muted">
          {loading ? 'Loading…' : `${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()} event(s)`}
        </div>

        <DataGrid<AuditLogRow>
          data={filtered}
          columns={columns}
          defaultPageSize={25}
          pageSizes={[25, 50, 100, 250]}
          defaultSort={{ columnAccessor: 'eventAt', direction: 'desc' }}
          onRowClick={(r) => setSelected(r)}
        />
      </Card>

      <DetailModal row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function DetailModal({ row, onClose }: { row: AuditLogRow | null; onClose: () => void }) {
  const open = !!row;
  const routeValues = useMemo(() => parseJsonish(row?.routeValuesJson), [row]);
  return (
    <Modal open={open} onClose={onClose} title={row ? `${row.name ?? row.actionName ?? 'Event'}` : ''} width={720}>
      {row && (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <Stat label="When" value={eventAtOf(row) ? new Date(eventAtOf(row)).toLocaleString() : '—'} />
            <Stat label="Actor" value={String(row.actorUsername ?? '—')} />
            <Stat label="IP" value={String(row.ipAddress ?? '—')} />
            <Stat label="Method · Path" value={`${row.httpMethod ?? ''} ${row.resourcePath ?? ''}`.trim() || '—'} />
            <Stat label="Controller · Action" value={`${row.controllerName ?? '—'} · ${row.actionName ?? ''}`} />
            <Stat label="Status" value={`${row.statusCode ?? '—'}${row.durationMs ? ` · ${row.durationMs}ms` : ''}`} />
            <Stat label="Severity" value={String(row.severity ?? '—')} />
            <Stat label="Correlation" value={String(row.correlationId ?? '—')} />
          </div>
          {row.errorMessage ? (
            <div>
              <div className="mb-1 text-xs font-medium text-danger">Error</div>
              <pre className="max-h-[200px] overflow-auto rounded-md border border-danger/40 bg-danger/5 p-3 font-mono text-[11px] text-danger">{String(row.errorMessage)}</pre>
            </div>
          ) : null}
          <div>
            <div className="mb-1 text-xs font-medium text-content-secondary">Route values</div>
            {routeValues ? (
              <pre className="max-h-[260px] overflow-auto rounded-md border border-border bg-surface-card-hover p-3 font-mono text-[11px]">{JSON.stringify(routeValues, null, 2)}</pre>
            ) : (
              <div className="rounded-md border border-border bg-surface-card-hover p-3 text-xs text-content-muted">No route values recorded.</div>
            )}
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-end border-t border-border pt-3">
        <Button onClick={onClose}><X className="h-4 w-4" /> Close</Button>
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-content-muted">{label}</div>
      <div className="break-all text-sm font-medium text-content-primary">{value || '—'}</div>
    </div>
  );
}

function SeverityPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  const colour = s === 'error' || s === 'critical'
    ? { cls: 'border-danger/40 bg-danger/10 text-danger', dot: 'bg-danger' }
    : s === 'warning'
      ? { cls: 'border-warning/40 bg-warning/10 text-warning', dot: 'bg-warning' }
      : { cls: 'border-info/40 bg-info/10 text-info', dot: 'bg-info' };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${colour.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${colour.dot}`} /> {v || '—'}
    </span>
  );
}

function relativeTime(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function parseJsonish(v: unknown): unknown {
  if (v == null || v === '') return null;
  if (typeof v === 'object') return v;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return v; } }
  return v;
}

function csvEscape(v: unknown): string {
  const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
